import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Heart, ArrowRight, Quote, Droplet, HandHeart, Cross, Stethoscope, ShieldAlert, ChevronRight,
} from "lucide-react";
import HeroCanvas from "@/components/HeroCanvas";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLine } from "@/components/Reveal";
import ProgressBar from "@/components/ProgressBar";
import PayPalHostedButton from "@/components/PayPalHostedButton";
import api from "@/api";
import { IMAGES, STATS } from "@/data/content";

const Home = () => {
  const [stats, setStats] = useState(null);
  const { scrollY } = useScroll();
  const heroImageY = useTransform(scrollY, [0, 600], [0, 60]);
  const canvasY = useTransform(scrollY, [0, 600], [0, 110]);

  useEffect(() => {
    api.get("/donations/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#F8FAFC] pt-28 sm:pt-36 pb-16 sm:pb-24">
        <div className="blob-float absolute -top-32 -left-32 h-96 w-96 rounded-full bg-sky-200/40 blur-3xl" aria-hidden="true" />
        <div className="blob-float absolute top-40 -right-24 h-80 w-80 rounded-full bg-[#FF6B6B]/10 blur-3xl" style={{ animationDelay: "-7s" }} aria-hidden="true" />

        <div className="container-site relative grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="inline-flex items-center gap-2 rounded-full border border-[#0284C7]/25 bg-[#EFF6FF] px-4 py-2 text-xs sm:text-sm font-bold uppercase tracking-[0.16em] text-[#0284C7]"
              data-testid="hero-eyebrow"
            >
              <Droplet size={15} /> Association — Maladies rares
            </motion.span>

            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-[#0F2C59]" data-testid="hero-title">
              <MaskedLine delay={0.25}>Ensemble, soutenons</MaskedLine>
              <MaskedLine delay={0.4}>
                les malades de <span className="text-[#0284C7]">Waldmann</span>
              </MaskedLine>
              <MaskedLine delay={0.55}>
                <span className="text-[#FF6B6B]">et leurs familles.</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600"
            >
              La maladie de Waldmann est une maladie chronique rare qui impose des perfusions et
              un suivi hospitalier chaque semaine. Aide au Monde et Santé accompagne les malades,
              informe le public et finance l'amélioration de la prise en charge.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link to="/faire-un-don" data-testid="hero-donate-button" className="btn-don don-pulse !px-8 !py-4 !text-lg">
                <Heart size={20} strokeWidth={2.5} fill="currentColor" />
                Faire un don
              </Link>
              <Link to="/la-maladie" data-testid="hero-discover-button" className="btn-outline-navy !px-7 !py-3.5">
                Découvrir la maladie <ArrowRight size={18} />
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="mt-5 max-w-xl text-sm sm:text-base font-medium leading-relaxed text-slate-600"
              data-testid="hero-cerfa-note"
            >
              Un reçu fiscal (CERFA) vous sera délivré dès réception de votre paiement.{" "}
              <Link
                to="/faire-un-don#cerfa"
                data-testid="hero-cerfa-link"
                className="font-bold text-[#0284C7] underline underline-offset-2 transition-colors hover:text-[#0F2C59]"
              >
                Remplir le formulaire pour recevoir votre CERFA
              </Link>
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#047857]"
            >
              <ShieldAlert size={16} className="text-[#10B981]" />
              66 % de votre don est déductible de vos impôts.
            </motion.p>
          </div>

          {/* Visuel : photo clinquée + goutte protectrice en parallaxe */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto w-full max-w-md lg:max-w-none"
          >
            <motion.div style={{ y: canvasY }} className="absolute -inset-10 sm:-inset-16">
              <HeroCanvas />
            </motion.div>
            <motion.figure
              style={{ y: heroImageY }}
              className="relative overflow-hidden rounded-[2rem] border-8 border-white shadow-2xl shadow-[#0F2C59]/20"
            >
              <img
                src={IMAGES.heroInfusion}
                alt="Soignant accompagnant avec bienveillance un patient lors d'une perfusion"
                className="aspect-[4/5] w-full object-cover"
                loading="eager"
                data-testid="hero-image"
              />
              <figcaption className="absolute inset-x-4 bottom-4 rounded-2xl bg-white/85 px-5 py-4 backdrop-blur-md">
                <p className="font-heading text-sm font-bold text-[#0F2C59]">
                  « Chaque semaine compte. Chaque don aussi. »
                </p>
                <p className="mt-1 text-xs font-medium text-slate-500">
                  Accompagnement des soins hebdomadaires
                </p>
              </figcaption>
            </motion.figure>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* CHIFFRES CLÉS */}
      <section className="py-16 md:py-24" aria-label="Chiffres clés">
        <div className="container-site">
          <Reveal>
            <span className="eyebrow">La maladie en chiffres</span>
            <h2 className="mt-4 max-w-2xl text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F2C59] leading-tight">
              Une maladie rare, des besoins très concrets
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.1}>
                <div data-testid={s.id} className="card-site h-full p-7">
                  <p className="font-heading text-2xl sm:text-3xl font-extrabold text-[#0284C7] whitespace-nowrap">{s.value}</p>
                  <p className="mt-2 font-heading text-base font-bold text-[#0F2C59]">{s.label}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUI SOMMES-NOUS */}
      <section className="bg-[#EFF6FF] py-16 md:py-24">
        <div className="container-site grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <span className="eyebrow">Qui sommes-nous</span>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F2C59] leading-tight">
              Une main tendue, chaque semaine, pour ne plus affronter la maladie seul
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600">
              Aide au Monde et Santé est née d'une conviction simple : personne ne devrait traverser
              une maladie rare dans l'isolement. Nous écoutons, informons et accompagnons les
              personnes atteintes de la maladie de Waldmann et leurs proches, du diagnostic au
              quotidien des soins.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                { icon: HandHeart, text: "Soutien moral et aide pratique aux malades et familles" },
                { icon: Cross, text: "Information du public et des professionnels de santé" },
                { icon: Droplet, text: "Financement de l'amélioration des soins et des perfusions" },
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#0284C7] shadow-sm">
                    <item.icon size={19} />
                  </span>
                  <p className="pt-2 text-base font-semibold text-slate-700">{item.text}</p>
                </li>
              ))}
            </ul>
            <Link to="/nos-actions" data-testid="home-actions-link" className="btn-outline-navy mt-9">
              Découvrir nos actions <ChevronRight size={17} />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2.2rem] bg-white/60" aria-hidden="true" />
              <img
                src={IMAGES.patientCare}
                alt="Bénévole prenant la main d'une personne âgée avec chaleur"
                className="relative aspect-[4/3] w-full rounded-[2rem] object-cover shadow-xl shadow-[#0F2C59]/10"
                loading="lazy"
                data-testid="home-about-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* TÉMOIGNAGE */}
      <section className="py-16 md:py-24" aria-label="Témoignage">
        <div className="container-site">
          <Reveal>
            <figure
              data-testid="home-testimonial"
              className="relative mx-auto max-w-3xl rounded-[2rem] bg-[#0F2C59] p-8 sm:p-14 text-center"
            >
              <Quote size={44} className="mx-auto text-[#FF6B6B]" aria-hidden="true" />
              <blockquote className="mt-6 font-heading text-xl sm:text-2xl font-bold leading-relaxed text-white">
                « Quand le diagnostic est tombé, on s'est sentis très seuls. L'association nous a
                écoutés, expliqués, rassurés. Aujourd'hui, les perfusions du mercredi font partie
                de notre vie — et on ne les vit plus jamais seuls. »
              </blockquote>
              <figcaption className="mt-7">
                <p className="font-heading text-base font-bold text-[#7DD3FC]">Marie L.</p>
                <p className="mt-1 text-sm text-sky-100/70">
                  Maman d'Enzo, 9 ans, atteint de la maladie de Waldmann
                </p>
                <p className="mt-2 text-[11px] uppercase tracking-[0.14em] text-sky-100/40">
                  Témoignage d'exemple — modifiable par l'association
                </p>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* BANDEAU DON */}
      <section className="pb-20 md:pb-28">
        <div className="container-site">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] bg-[#ECFDF5] p-8 sm:p-14">
              <div className="grid items-center gap-10 lg:grid-cols-2">
                <div>
                  <span className="eyebrow !text-[#047857]">
                    <Stethoscope size={15} /> Objectif 2026
                  </span>
                  <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F2C59] leading-tight">
                    Financer des aides concrètes pour les malades
                  </h2>
                  <p className="mt-5 text-base leading-relaxed text-slate-600">
                    Soutien alimentaire et soutien financier au quotidien : chaque euro donné se
                    transforme en aide concrète pour les personnes atteintes de la maladie de
                    Waldmann et leurs familles.
                  </p>
                  <Link to="/faire-un-don" data-testid="home-donate-cta" className="btn-don don-pulse mt-8">
                    <Heart size={18} strokeWidth={2.5} fill="currentColor" /> Je fais un don
                  </Link>
                  <p className="mt-5 text-sm sm:text-base font-medium leading-relaxed text-slate-600" data-testid="home-donate-cerfa-note">
                    Un reçu fiscal (CERFA) vous sera délivré dès réception de votre paiement.{" "}
                    <Link
                      to="/faire-un-don#cerfa"
                      data-testid="home-donate-cerfa-link"
                      className="font-bold text-[#0284C7] underline underline-offset-2 transition-colors hover:text-[#0F2C59]"
                    >
                      Remplir le formulaire pour recevoir votre CERFA
                    </Link>
                  </p>
                </div>
                <div className="rounded-2xl bg-white p-7 shadow-sm">
                  <ProgressBar stats={stats} />
                  <div className="mt-6 border-t border-slate-100 pt-6">
                    <p className="mb-4 text-center text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                      Don rapide et sécurisé
                    </p>
                    <PayPalHostedButton testid="paypal-hosted-button-home" />
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
